// StickiesListPanelMain.js - Updated with header reorder arrows
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import './StickiesListPanelMain.css';
import AICompositionModal from '../AICompositionModal';
import StickiesTable from './StickiesTable';
import { sendStickiesToAIComposition, COMPOSITION_MODES } from '../../api/aiComposition';

const StickiesListPanelMain = ({ 
  stickies, 
  onExpandSticky, 
  displayModes = {},
  onToggleDisplayMode,
  tagDomains = {} ,
  selectedStickies = {}, 
  onSelectChange, 
  onSaveLayout = () => console.warn('onSaveLayout not provided'),
  onRemoveSticky = () => console.warn('onRemoveSticky not provided'),
  onReorderSticky = () => console.warn('onReorderSticky not provided'),
  updateSticky = () => console.warn('updateSticky not provided'),
  isRightPanelVisible = false
}) => {
  const [height, setHeight] = useState(30);
  const [isResizing, setIsResizing] = useState(false);
  const startResizeHeight = useRef(0);
  const startResizeY = useRef(0);
  const previousHeight = useRef(150);
  const [freezeButtonText, setFreezeButtonText] = useState('Freeze');
  const [isAICompositionModalOpen, setIsAICompositionModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null); // NEW: Track selected row
  
  // Memoize visible stickies to prevent unnecessary re-renders
  const visibleStickies = useMemo(() => {
    return stickies
      .filter(sticky => 
        !displayModes[sticky.id] || 
        displayModes[sticky.id] === 'list' || 
        displayModes[sticky.id] === 'both'
      )
      .sort((a, b) => (a.list_order || 0) - (b.list_order || 0));
  }, [stickies, displayModes]);

  const MIN_HEIGHT = 30;
  const MAX_HEIGHT = 300;

  // NEW: Row selection handler
  const handleRowSelect = useCallback((stickyId) => {
    setSelectedRowId(selectedRowId === stickyId ? null : stickyId);
  }, [selectedRowId]);

  // NEW: Header reorder handler
  const handleHeaderReorder = useCallback((direction) => {
    if (!selectedRowId) return;
    
    const currentIndex = visibleStickies.findIndex(s => s.id === selectedRowId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= visibleStickies.length) return;

    const reorderedStickies = [...visibleStickies];
    const [movedSticky] = reorderedStickies.splice(currentIndex, 1);
    reorderedStickies.splice(newIndex, 0, movedSticky);

    const updates = reorderedStickies.map((sticky, index) => ({
      id: sticky.id,
      list_order: index
    }));

    onReorderSticky(updates);
  }, [selectedRowId, visibleStickies, onReorderSticky]);

  // Title update handler
  const handleTitleUpdate = useCallback((stickyId, newTitle) => {
    updateSticky(stickyId, { title: newTitle });
  }, [updateSticky]);

  // Tag update handler
  const handleTagUpdate = useCallback((stickyId, tagIndex, newTag) => {
    const tagField = tagIndex === 0 ? 'tag1' : 'tag2';
    updateSticky(stickyId, { [tagField]: newTag });
    console.log(`Updated ${tagField} for sticky ${stickyId} to: ${newTag}`);
  }, [updateSticky]);

  // Resize handlers
const handleHeaderDoubleClick = useCallback((e) => {
  // Don't collapse if double-clicking on button areas
  if (e.target.closest('.header-buttons') ||  
      e.target.closest('.header-reorder-section') ||
      e.target.closest('button')) {
    return;
  }
  
  if (height > MIN_HEIGHT) {
    previousHeight.current = height;
    setHeight(MIN_HEIGHT);
  } else {
    setHeight(Math.min(previousHeight.current, MAX_HEIGHT));
  }
}, [height]);;

  const handleResize = useCallback((e) => {
    if (!isResizing) return;
    const deltaY = startResizeY.current - e.clientY;
    const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, startResizeHeight.current + deltaY));
    setHeight(newHeight);
  }, [isResizing]);

  const handleResizeStart = useCallback((e) => {
  // Don't start resize if clicking on button areas
  if (e.target.closest('.header-buttons') || 
      e.target.closest('.header-reorder-section') ||
      e.target.closest('button')) {
    return;
  }
  
  setIsResizing(true);
  startResizeHeight.current = height;
  startResizeY.current = e.clientY;
  document.body.classList.add('list-panel-resizing-main');
}, [height]);

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      document.body.classList.remove('list-panel-resizing-main');
    }
  }, [isResizing]);

  // Other handlers
  const handleFreezeAll = useCallback(() => {
    const stickyUpdates = stickies.map(sticky => ({
      id: sticky.id,
      x: sticky.x,
      y: sticky.y,
      width: sticky.width,
      height: sticky.height,
      main_x: sticky.main_x,
      main_y: sticky.main_y,
      main_width: sticky.main_width,
      main_height: sticky.main_height,
      display_mode: displayModes[sticky.id] || 'both',
      z_index: sticky.zIndex
    }));

    setFreezeButtonText('Saved!');
    setTimeout(() => {
      setFreezeButtonText('Freeze');
    }, 2000);
    
    onSaveLayout(stickyUpdates);
  }, [stickies, displayModes, onSaveLayout]);

  const handleMinimizeAll = useCallback(() => {
    console.log('Minimizing all unselected stickies...');
    stickies.forEach(sticky => {
      if (!selectedStickies[sticky.id]) {
        console.log(`Minimizing sticky ${sticky.id}`);
        onToggleDisplayMode(sticky.id, 'list');
      }
    });
  }, [stickies, selectedStickies, onToggleDisplayMode]);

  const handleStickyClick = useCallback((sticky) => {
    if (displayModes[sticky.id] === 'list') {
      onToggleDisplayMode(sticky.id, 'both');
    }
    onExpandSticky(sticky.id);
  }, [displayModes, onToggleDisplayMode, onExpandSticky]);

  const handleStickySelect = useCallback((stickyId, isSelected) => {
    onSelectChange(stickyId, isSelected);
  }, [onSelectChange]);

  const getSelectedStickyIdsArray = useCallback(() => {
    const selectedIds = Object.entries(selectedStickies)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);
    
    return selectedIds;
  }, [selectedStickies]);

  const handleAICompositionClick = useCallback(() => {
    setIsAICompositionModalOpen(true);
  }, []);

  const handleSendToAI = useCallback(async (selectedStickies, mode = COMPOSITION_MODES.COMPOSE_NOTE) => {
    try {
      console.log('Sending to AI composition:', selectedStickies);
      
      const stickiesWithTags = stickies.filter(sticky => 
        selectedStickies.includes(sticky.id)
      );
      
      const noteContext = {
        note_id: 1,
        note_title: "AI Composition Result"
      };
      
      const result = await sendStickiesToAIComposition(
        stickiesWithTags, 
        mode, 
        noteContext
      );
      
      console.log('AI Composition Result:', result);
      setIsAICompositionModalOpen(false);
      
    } catch (error) {
      console.error('AI composition failed:', error);
      alert(`AI composition failed: ${error.message}`);
    }
  }, [stickies]);

  const handleDeleteSelected = useCallback(async () => {
    const selectedIds = getSelectedStickyIdsArray();
    if (selectedIds.length === 0) return;
    
    if (window.confirm(`Delete ${selectedIds.length} selected stickies?`)) {
      for (const stickyId of selectedIds) {
        try {
          await onRemoveSticky(stickyId);
          onSelectChange(stickyId, false);
        } catch (error) {
          console.error(`Failed to delete sticky ${stickyId}:`, error);
        }
      }
    }
  }, [getSelectedStickyIdsArray, onRemoveSticky, onSelectChange]);

  const getStickyIcon = useCallback((type) => {
    switch (type) {
      case 'ai': return '🤖';
      case 'checklist': return '☑️';
      default: return '📄';
    }
  }, []);

  // Get selected row info for display
  const selectedRowInfo = useMemo(() => {
    if (!selectedRowId) return null;
    const selectedSticky = visibleStickies.find(s => s.id === selectedRowId);
    if (!selectedSticky) return null;
    
    const index = visibleStickies.findIndex(s => s.id === selectedRowId);
    return {
      sticky: selectedSticky,
      index,
      canMoveUp: index > 0,
      canMoveDown: index < visibleStickies.length - 1
    };
  }, [selectedRowId, visibleStickies]);

  // Effects
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize);
      window.addEventListener('mouseup', handleResizeEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleResize);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResize, handleResizeEnd]);

  return (
    <div 
      className={`stickies-list-panel-main ${isRightPanelVisible ? 'right-panel-visible' : 'panel-closed'}`}
      style={{ height: `${height}px` }}
    >
      <div 
        className="list-header-main list-panel-resize-header-main"
        onMouseDown={handleResizeStart}
        onDoubleClick={handleHeaderDoubleClick}
      >
        <div className="list-panel-resize-handle-main" />
        
        {/* LEFT SIDE: Buttons + Reorder Controls */}
        <div className="header-left-section">
          <div className="header-buttons">
            <button 
              className="minimize-all-button-main"
              onClick={handleMinimizeAll}
              title="Minimize all unselected stickies"
            >
              Minimize All
            </button>
            <button
              className="freeze-all-button-main"
              onClick={handleFreezeAll}
              title="Save current sticky positions and states"
            >
              {freezeButtonText}
            </button>
            <button 
              className="ai-composition-button-main"
              onClick={handleAICompositionClick}
              disabled={getSelectedStickyIdsArray().length === 0}
              title="Create AI composition from selected stickies"
            >
              AI Composition ({getSelectedStickyIdsArray().length})
            </button>
            <button 
              className="delete-selected-button-main"
              onClick={handleDeleteSelected}
              disabled={getSelectedStickyIdsArray().length === 0}
              title={`Delete ${getSelectedStickyIdsArray().length} selected stickies`}
            >
              Delete ({getSelectedStickyIdsArray().length})
            </button>
          </div>

          {/* NEW: Reorder Controls */}
          <div className="header-reorder-section">
            <button 
              className="header-reorder-btn up"
              onClick={() => handleHeaderReorder('up')}
              disabled={!selectedRowInfo || !selectedRowInfo.canMoveUp}
              title={selectedRowInfo ? `Move "${selectedRowInfo.sticky.title || 'Untitled'}" up` : 'Select a row to reorder'}
            >
              ▲
            </button>
            <button 
              className="header-reorder-btn down"
              onClick={() => handleHeaderReorder('down')}
              disabled={!selectedRowInfo || !selectedRowInfo.canMoveDown}
              title={selectedRowInfo ? `Move "${selectedRowInfo.sticky.title || 'Untitled'}" down` : 'Select a row to reorder'}
            >
              ▼
            </button>
          </div>
        </div>

        {/* RIGHT SIDE: Title and count + Selected info */}
        <div className="header-right-section">
          {selectedRowInfo && (
            <span className="selected-row-info">
              Selected: "{selectedRowInfo.sticky.title || 'Untitled'}" (#{selectedRowInfo.index + 1})
            </span>
          )}
          <h3 style={{ margin: 0 }}>Notes ({visibleStickies.length})</h3>
        </div>
      </div>
      
      <StickiesTable
        stickies={visibleStickies}
        tagDomains={tagDomains}
        selectedStickies={selectedStickies}
        displayModes={displayModes}
        selectedRowId={selectedRowId}
        onStickyClick={handleStickyClick}
        onStickySelect={handleStickySelect}
        onRowSelect={handleRowSelect}
        onTitleUpdate={handleTitleUpdate} 
        onTagUpdate={handleTagUpdate}
        getStickyIcon={getStickyIcon}
      />

      <AICompositionModal
        isOpen={isAICompositionModalOpen}
        onClose={() => setIsAICompositionModalOpen(false)}
        stickies={stickies}
        selectedStickyIds={getSelectedStickyIdsArray()}
        onSendToAI={handleSendToAI}
      />
    </div>
  );
};

export default StickiesListPanelMain;